import { useState } from "react";
import { Box, Divider, Option, Select, Typography } from "@mui/joy";
import { Check, Link, Link2 } from "lucide-react";
import { Button, IconButton, Alert, ControlledTextField, FormRow } from "../../../../../../shared";
import { useEventHandler } from "../../hooks";

const PROJECT_TYPES = ["Residential", "Commercial", "Industrial", "Multifamily"] as const;

const PROJECT_STATUS_LABELS: Record<string, string> = {
    "NOT-STARTED": "Not Started",
    "IN-PROGRESS": "In Progress",
    COMPLETED: "Completed",
    CANCELLED: "Cancelled",
};

function formatProjectStatus(status?: string | null) {
    if (!status) return "Not Started";
    return PROJECT_STATUS_LABELS[status] ?? status;
}

export function Basic({ details }: { details: any | null }) {
    const {
        slug,
        setSlug,
        projectType,
        setProjectType,
        secondarySalesmanName,
        setSecondarySalesmanName,
        eagleViewOrderId,
        setEagleViewOrderId,
        saving,
        error,
        success,
        hasChanges,
        handleUpdate,
    } = useEventHandler(details);

    const [slugCopied, setSlugCopied] = useState(false);

    const copySlug = async () => {
        if (!slug) return;
        try {
            await navigator.clipboard.writeText(slug);
            setSlugCopied(true);
            window.setTimeout(() => setSlugCopied(false), 1500);
        } catch {
            // Clipboard may be unavailable in some contexts
        }
    };

    return (
        <Box sx={{ flex: 1 }}>
            <Box mb={1}>
                <Typography level="title-sm" component="div">
                    Basic Information
                    <Typography level="body-xs" sx={{ opacity: 0.8, ml: 1 }} component="span">
                        {details?.Id}
                    </Typography>
                </Typography>
                <Typography level="body-sm">You can edit the basic information of the project seamlessly.</Typography>
            </Box>

            {error && <Alert errorMessage={error} />}
            {success && <Alert successMessage={success} />}

            <Divider sx={{ mb: 3, mt: 2 }} />
            <Box display="flex" flexDirection="column" gap={2}>
                <FormRow label="EagleView Order ID" description="Project Id / EagleView order Id.">
                    <ControlledTextField
                        disabled={!!details?.EagleViewOrderId}
                        value={eagleViewOrderId}
                        onChange={setEagleViewOrderId}
                    />
                </FormRow>
                <FormRow label="Lead Source" description="Primary source of this lead.">
                    <ControlledTextField value={details?.Lead?.LeadSource?.Name ?? "Not specified"} disabled />
                </FormRow>
                {details?.Lead?.Name && (
                    <FormRow label="Secondary Lead" description="Person or company who brought this lead.">
                        <ControlledTextField value={details.Lead.Name} disabled />
                    </FormRow>
                )}

                <FormRow label="Status" description="Current project status (managed by workflows).">
                    <ControlledTextField value={formatProjectStatus(details?.Status)} disabled />
                </FormRow>
                <Divider sx={{ my: 1 }} />

                <FormRow label="Project Slug" description="This will be used to access the project.">
                    <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                        <ControlledTextField
                            startDecorator={<Link2 size={16} />}
                            value={slug}
                            onChange={setSlug}
                            sx={{ flex: 1 }}
                        />
                        <IconButton onClick={copySlug} disabled={!slug} title="Copy slug">
                            {slugCopied ? <Check size={16} /> : <Link size={16} />}
                        </IconButton>
                    </Box>
                </FormRow>
                <FormRow label="Project Type" description="Residential, commercial, industrial, or multifamily.">
                    <Select
                        size="sm"
                        variant="outlined"
                        value={projectType || null}
                        placeholder="Select type"
                        onChange={(_, value) => setProjectType((value as string) ?? "")}
                        sx={{
                            borderRadius: "4px",
                            boxShadow: "none",
                            bgcolor: "background.surface",
                        }}
                    >
                        {[
                            ...PROJECT_TYPES,
                            ...(projectType && !(PROJECT_TYPES as readonly string[]).includes(projectType)
                                ? [projectType]
                                : []),
                        ].map((type) => (
                            <Option key={type} value={type}>
                                {type}
                            </Option>
                        ))}
                    </Select>
                </FormRow>
                <Divider sx={{ my: 1 }} />

                <FormRow label="Salesman Name" description="Primary sales representative.">
                    <ControlledTextField value={details?.SalesmanName ?? ""} disabled />
                </FormRow>
                <FormRow label="Secondary Salesman Name" description="Additional sales representative.">
                    <ControlledTextField
                        value={secondarySalesmanName}
                        onChange={setSecondarySalesmanName}
                        placeholder="Enter secondary salesman name"
                    />
                </FormRow>

                <Box>
                    <Button
                        onClick={handleUpdate}
                        disabled={!hasChanges || saving}
                        loading={saving}
                        loadingPosition="end"
                        sx={{ borderRadius: "4px" }}
                    >
                        {saving ? "Updating" : "Update"}
                    </Button>
                </Box>
            </Box>
        </Box>
    );
}
